import type { UseFormReturn } from 'react-hook-form';
import {
  type EditApiTokenFormValues,
  type UpdateApiTokenRequestDto,
} from './types';
import { useTranslation } from 'react-i18next';
import { useUpdateApiToken } from '../api';
import { useToast } from '@/shared/lib';
import { useCallback } from 'react';
import { useTwoFactorAction } from '@/entities/mfa-identity';

type Props = {
  form: UseFormReturn<EditApiTokenFormValues>;
  apiTokenId: string;
  accountNumber: string;
  onBackToEditApiTokenModal: () => void;
  onBackToApiTokensModal: () => void;
};

type Params = {
  updateApiToken: () => Promise<void>;
  isPending: boolean;
};

export const useEditApiTokenFlow = ({
  form,
  apiTokenId,
  accountNumber,
  onBackToEditApiTokenModal,
  onBackToApiTokensModal,
}: Props): Params => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: updateApiTokenMutation, isPending } = useUpdateApiToken({
    throwOnError: false,
  });

  const { execute } = useTwoFactorAction<void, UpdateApiTokenRequestDto>({
    requestFn: args => updateApiTokenMutation(args),
    onBack: onBackToEditApiTokenModal,
    onSuccess: () => {
      toast.success(t('webApiTokens.editing.result.success'));
      onBackToApiTokensModal();
    },
    onError: () => {
      toast.error(t('webApiTokens.editing.result.error'));
    },
  });

  const updateApiToken = useCallback(async () => {
    const values = form.getValues();

    if (!values.access || !values.authenticationType) return;

    await execute({
      webapiTokenid: apiTokenId,
      accountNumber,
      access: values.access,
      authenticationType: values.authenticationType,
      twoFA: null,
    });
  }, [form, apiTokenId, accountNumber, execute]);

  return {
    updateApiToken,
    isPending,
  };
};
